import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowDown, ArrowLeft, ArrowRight, Camera, Heart } from 'lucide-react';

/* ── Animation Variants ── */
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

/* ── Animated Counter ── */
function AnimNum({ target, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [val, setVal] = useState(0);
  const num = parseFloat(String(target).replace(/[^0-9.]/g, ''));
  useEffect(() => {
    if (!inView) return;
    let current = 0;
    const duration = 2000;
    const step = Math.max(0.5, num / (duration / 16));
    const t = setInterval(() => {
      current += step;
      if (current >= num) { setVal(num); clearInterval(t); } else setVal(current);
    }, 16);
    return () => clearInterval(t);
  }, [inView, num]);
  return (
    <span ref={ref}>
      {num >= 1000 ? Math.round(val).toLocaleString() : Math.round(val)}
      {suffix}
    </span>
  );
}

/* ── Sticky Side Nav ── */
const NAV_SECTIONS = [
  { id: 'as-hero', label: 'Hero' },
  { id: 'as-overview', label: 'Overview' },
  { id: 'as-challenge', label: 'Challenge' },
  { id: 'as-results', label: 'Results' },
  { id: 'as-gallery', label: 'Gallery' },
  { id: 'as-observations', label: 'Observations' },
  { id: 'as-timeline', label: 'Timeline' },
  { id: 'as-cta', label: 'Contact' },
];

function StickyNav({ activeId }) {
  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-3" data-testid="as-sticky-nav">
      {NAV_SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`text-[10px] tracking-widest uppercase transition-all duration-300 ${
            activeId === s.id ? 'text-[#C8A96A] font-medium' : 'text-muted-foreground/40 hover:text-muted-foreground'
          }`}
        >
          {s.label}
        </a>
      ))}
    </div>
  );
}

/* ── Service Chip ── */
function ServiceChip({ label, delay = 0 }) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.4 }}
      className="px-5 py-2.5 border border-border text-sm tracking-widest uppercase text-foreground rounded-full"
    >
      {label}
    </motion.span>
  );
}

/* ── Metric Card ── */
function MetricCard({ value, label, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="text-center"
    >
      <div className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#C8A96A] mb-2">
        <AnimNum target={String(value)} />
      </div>
      <p className="text-xs sm:text-sm tracking-widest uppercase text-muted-foreground">{label}</p>
    </motion.div>
  );
}

/* ── Timeline Item ── */
function TimelineItem({ year, label, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      className="flex flex-col items-center text-center"
    >
      <div className="w-14 h-14 rounded-full bg-[#C8A96A]/10 border border-[#C8A96A]/30 flex items-center justify-center mb-3">
        <span className="font-serif text-sm font-medium text-[#C8A96A]">{year}</span>
      </div>
      <p className="text-sm font-medium tracking-wide">{label}</p>
      {index < total - 1 && <div className="w-px h-8 bg-border mt-3 md:hidden" />}
    </motion.div>
  );
}

/* ── Premium Photo Slideshow ── */
function PhotoSlideshow({ images }) {
  const [idx, setIdx] = useState(0);
  const touchStart = useRef(null);
  const goNext = useCallback(() => setIdx((p) => (p + 1) % images.length), [images.length]);
  const goPrev = useCallback(() => setIdx((p) => (p - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [goNext, goPrev]);

  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
    touchStart.current = null;
  };

  if (!images.length) return null;
  return (
    <div data-testid="as-slideshow">
      <div className="max-w-6xl mx-auto mb-8" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={idx}
              src={images[idx].src}
              alt={`Aude Swim Photography ${idx + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full h-[55vh] md:h-[70vh] object-contain mx-auto"
            />
          </AnimatePresence>
        </div>
        <div className="flex items-center justify-between mt-6 px-2">
          <button onClick={goPrev} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm tracking-widest uppercase" data-testid="slideshow-prev">
            <ArrowLeft className="h-5 w-5" /> Previous
          </button>
          <span className="text-sm text-muted-foreground font-medium tracking-widest">{idx + 1} / {images.length}</span>
          <button onClick={goNext} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm tracking-widest uppercase" data-testid="slideshow-next">
            Next <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex gap-2 justify-center flex-wrap max-w-6xl mx-auto px-4">
        {images.map((img, i) => (
          <button
            key={img.postNum}
            onClick={() => setIdx(i)}
            className={`w-14 h-14 overflow-hidden border transition-all duration-300 ${
              idx === i ? 'border-[#C8A96A] ring-1 ring-[#C8A96A]/30' : 'border-border hover:border-[#C8A96A]/40'
            }`}
          >
            <img src={img.src} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   DATA
   ══════════════════════════════════════════ */
const SERVICES = ['Photography'];

const GALLERY_IMAGES = [
  { postNum: 1, src: '/images/aude-post-1.jpg' },
  { postNum: 2, src: '/images/aude-post-2.jpg' },
  { postNum: 4, src: '/images/aude-post-4.jpg' },
  { postNum: 5, src: '/images/aude-post-5.jpg' },
  { postNum: 6, src: '/images/aude-post-6.jpg' },
  { postNum: 7, src: '/images/aude-post-7.jpg' },
  { postNum: 8, src: '/images/aude-post-8.jpg' },
  { postNum: 9, src: '/images/aude-post-9.jpg' },
  { postNum: 10, src: '/images/aude-post-10.jpg' },
  { postNum: 11, src: '/images/aude-post-11.jpg' },
  { postNum: 12, src: '/images/aude-post-12.jpg' },
  { postNum: 13, src: '/images/aude-post-13.jpg' },
  { postNum: 14, src: '/images/aude-post-14.jpg' },
  { postNum: 15, src: '/images/aude-post-15.jpg' },
  { postNum: 16, src: '/images/aude-post-16.jpg' },
  { postNum: 17, src: '/images/aude-post-17.jpg' },
  { postNum: 19, src: '/images/aude-post-19.jpg' },
  { postNum: 20, src: '/images/aude-post-20.jpg' },
  { postNum: 22, src: '/images/aude-post-22.jpg' },
  { postNum: 23, src: '/images/aude-post-23.jpg' },
  { postNum: 24, src: '/images/aude-post-24.jpg' },
  { postNum: 25, src: '/images/aude-post-25.jpg' },
  { postNum: 26, src: '/images/aude-post-26.jpg' },
  { postNum: 27, src: '/images/aude-post-27.jpg' },
  { postNum: 28, src: '/images/aude-post-28a.jpg' },
  { postNum: 29, src: '/images/aude-post-29.jpg' },
  { postNum: 30, src: '/images/aude-post-30.jpg' },
  { postNum: 31, src: '/images/aude-post-31.jpg' },
  { postNum: 33, src: '/images/aude-post-33.jpg' },
  { postNum: 34, src: '/images/aude-post-34.jpg' },
  { postNum: 37, src: '/images/aude-post-37.jpg' },
  { postNum: 38, src: '/images/aude-post-38.jpg' },
  { postNum: 39, src: '/images/aude-post-39.jpg' },
  { postNum: 40, src: '/images/aude-post-40.jpg' },
  { postNum: 41, src: '/images/aude-post-41.jpg' },
  { postNum: 42, src: '/images/aude-post-42.jpg' },
  { postNum: 43, src: '/images/aude-post-43.jpg' },
  { postNum: 44, src: '/images/aude-post-44.jpg' },
  { postNum: 45, src: '/images/aude-post-45.jpg' },
  { postNum: 46, src: '/images/aude-post-46.jpg' },
  { postNum: 47, src: '/images/aude-post-47.jpg' },
  { postNum: 48, src: '/images/aude-post-48.jpg' },
  { postNum: 49, src: '/images/aude-post-49.jpg' },
  { postNum: 50, src: '/images/aude-post-50.jpg' },
  { postNum: 51, src: '/images/aude-post-51.jpg' },
];

const TOP_PHOTOS = [
  { rank: 1, src: '/images/aude-post-9.jpg', likes: 3429, title: 'Highest Likes' },
  { rank: 2, src: '/images/aude-post-1.jpg', likes: 2096, title: 'Second Highest Likes' },
  { rank: 3, src: '/images/aude-post-41.jpg', likes: 1135, title: 'Third Highest Likes' },
];

const RESULT_METRICS = [
  { value: 50, label: 'Photo Posts Reviewed' },
  { value: 3429, label: 'Highest Photo Likes' },
  { value: 23800, label: 'Visible Photo Likes' },
  { value: 504, label: 'Visible Photo Comments' },
  { value: 517, label: 'Average Likes Per Photo' },
];

const OBSERVATIONS = [
  'The highest-performing photo featured a three-model editorial composition.',
  'Couple photography appeared among several of the strongest-performing posts.',
  'Editorial portraits consistently appeared throughout the campaign.',
  'Group imagery generated some of the highest visible engagement.',
  'Lifestyle environments including beaches and pools were frequently represented across top-performing content.',
];

const TIMELINE_DATA = [
  { year: '2020', label: 'Campaign Photography' },
  { year: '2021', label: 'Collection Photography' },
  { year: '2022', label: 'Editorial Campaigns' },
  { year: '2023', label: 'Continued Photography' },
];

/* ════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ════════════════════════════════════════════ */
export default function AudeSwimPage() {
  const [activeSection, setActiveSection] = useState('as-hero');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-40% 0px -40% 0px' }
    );
    NAV_SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-0" data-testid="aude-swim-page">
      <StickyNav activeId={activeSection} />

      {/* ═══════ HERO ═══════ */}
      <section id="as-hero" ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-foreground" data-testid="as-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img src="/images/aude-post-9.jpg" alt="Aude Swim Campaign" className="w-full h-full object-contain opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/60 to-foreground/40" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeIn} className="text-xs tracking-[0.4em] uppercase text-[#C8A96A] mb-6">Photography Performance Case Study</motion.p>
            <motion.h1 variants={fadeIn} className="font-serif text-5xl sm:text-6xl lg:text-8xl font-medium text-background leading-[1.05] mb-6">
              Aude Swim
            </motion.h1>
            <motion.div variants={fadeIn} className="flex items-center justify-center gap-3 mb-8">
              <span className="text-xs tracking-widest uppercase text-background/50">Photography</span>
              <span className="text-background/30">•</span>
              <span className="text-xs tracking-widest uppercase text-background/50">2020–2023</span>
            </motion.div>
            <motion.p variants={fadeIn} className="text-background/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              A retrospective analysis of publicly visible Instagram engagement across 50 published photographs captured by Muse Social Creative.
            </motion.p>
            <motion.a
              variants={fadeIn}
              href="#as-overview"
              className="inline-flex items-center gap-2 text-[#C8A96A] text-sm tracking-widest uppercase hover:text-[#b8995a] transition-colors"
            >
              View Full Case Study
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <ArrowDown className="h-4 w-4" />
              </motion.span>
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════ OVERVIEW + SERVICES ═══════ */}
      <section id="as-overview" className="py-28 md:py-36" data-testid="as-overview">
        <div className="container-main">
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Overview</motion.p>
              <motion.h2 variants={fadeIn} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-10">
                Between 2020 and 2023, Muse Social Creative photographed multiple campaigns for Aude Swim.
              </motion.h2>
              <motion.p variants={fadeIn} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
                To better understand how the imagery resonated with audiences, we reviewed every published Instagram post featuring our photography and documented the publicly visible engagement metrics. Rather than relying on opinions, this case study focuses on measurable performance.
              </motion.p>
            </motion.div>

            {/* Services */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-16">
              <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Services</motion.p>
              <div className="flex flex-wrap gap-3">
                {SERVICES.map((s, i) => (
                  <ServiceChip key={s} label={s} delay={i * 0.06} />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ CHALLENGE ═══════ */}
      <section id="as-challenge" className="py-28 md:py-36" data-testid="as-challenge">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 max-w-6xl mx-auto items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Challenge</motion.p>
              <motion.h2 variants={fadeIn} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08]">
                Measuring the impact.
              </motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6">
              <motion.p variants={fadeIn} className="text-muted-foreground text-base md:text-lg leading-relaxed">
                Documenting the publicly visible engagement metrics of campaign photography to provide a transparent, data-driven look at how the imagery performed once published. The case study needed to:
              </motion.p>
              <motion.ul variants={fadeIn} className="space-y-4">
                {[
                  'Review every published Instagram photo post featuring Muse photography',
                  'Document publicly visible likes, comments, and engagement patterns',
                  'Identify the highest-performing images and common visual themes',
                  'Provide a transparent performance record across four years of work',
                  'Demonstrate measurable value through real audience engagement data',
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96A] mt-2.5 flex-shrink-0" />
                    <span className="text-foreground text-sm md:text-base leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ RESULTS ═══════ */}
      <section id="as-results" className="py-28 md:py-36" data-testid="as-results">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Results</motion.p>
            <motion.h2 variants={fadeIn} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">
              Numbers that speak for themselves.
            </motion.h2>
          </motion.div>

          {/* Performance Metrics */}
          <div className="max-w-5xl mx-auto mb-24">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="border border-border p-10 md:p-14"
            >
              <h3 className="font-serif text-2xl md:text-3xl font-medium mb-2 text-center">Photo Performance</h3>
              <p className="text-muted-foreground text-sm text-center mb-12 tracking-wide">Publicly Visible Instagram Engagement</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
                {RESULT_METRICS.map((m, i) => (
                  <MetricCard key={m.label} value={m.value} label={m.label} delay={i * 0.1} />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Top 3 Performing Photos — Full Images */}
          <div className="max-w-6xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
              <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Top Performing Photography</motion.p>
              <motion.h3 variants={fadeIn} className="font-serif text-2xl sm:text-3xl font-medium">
                Highest engagement.
              </motion.h3>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {TOP_PHOTOS.map((photo, i) => (
                <motion.div
                  key={photo.rank}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="group"
                >
                  <div className="relative overflow-hidden mb-6">
                    <img
                      src={photo.src}
                      alt={photo.title}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#C8A96A] mb-2">{photo.title}</p>
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Heart className="h-3.5 w-3.5 text-[#C8A96A]" /> {photo.likes.toLocaleString()} Likes
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ GALLERY SLIDESHOW ═══════ */}
      <section id="as-gallery" className="py-28 md:py-36 bg-secondary" data-testid="as-gallery">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
            <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Selected Work</motion.p>
            <motion.h2 variants={fadeIn} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">
              The photography.
            </motion.h2>
          </motion.div>
          <PhotoSlideshow images={GALLERY_IMAGES} />
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ OBSERVATIONS ═══════ */}
      <section id="as-observations" className="py-28 md:py-36" data-testid="as-observations">
        <div className="container-main">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Analysis</motion.p>
              <motion.h2 variants={fadeIn} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight">
                Performance Observations
              </motion.h2>
            </motion.div>
            <motion.ul initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-5">
              {OBSERVATIONS.map((obs, i) => (
                <motion.li key={i} variants={fadeIn} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96A] mt-2.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-base leading-relaxed">{obs}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ TIMELINE ═══════ */}
      <section id="as-timeline" className="py-28 md:py-36 bg-secondary" data-testid="as-timeline">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeIn} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Project Timeline</motion.p>
            <motion.h2 variants={fadeIn} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">
              Four years of photography.
            </motion.h2>
          </motion.div>
          <div className="relative max-w-3xl mx-auto">
            <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-border" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-3">
              {TIMELINE_DATA.map((t, i) => (
                <TimelineItem key={t.year} {...t} index={i} total={TIMELINE_DATA.length} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ CTA ═══════ */}
      <section id="as-cta" className="py-28 md:py-40 bg-foreground text-background" data-testid="as-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-3xl mx-auto">
            <motion.p variants={fadeIn} className="text-xs tracking-[0.4em] uppercase text-[#C8A96A] mb-6">Start a project</motion.p>
            <motion.h2 variants={fadeIn} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-background leading-tight mb-8">
              Looking for photography that elevates your brand?
            </motion.h2>
            <motion.div variants={fadeIn}>
              <Link
                to="/book-a-call"
                className="inline-flex items-center gap-2 bg-[#C8A96A] text-foreground font-medium text-sm px-12 py-4 rounded-full hover:bg-[#b8995a] transition-colors duration-300"
                data-testid="as-cta-btn"
              >
                Start Your Project
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
